import { z } from 'zod';
import type { Session } from '@supabase/supabase-js';
import { adressePhotoProfil } from '@/shared/comptes/photo';
import { ecrireCache, lireCache } from '@/shared/hors-ligne';
import { supabase } from '@/shared/supabase/client';
import { messageDeRefus } from './messages';
import type { EtatSession, ResultatAuth, ServiceAuth, Utilisateur } from './types';

/* La ligne `profils` telle que la renvoie PostgREST, vérifiée avant usage : une
   colonne renommée côté base doit casser ici, lisiblement, pas plus loin. */
const ligneProfil = z.object({
  id: z.string(),
  email: z.string(),
  nom: z.string(),
  /* Avec des valeurs par défaut : la copie gardée hors ligne peut dater d'avant. */
  prenom: z.string().default(''),
  nom_famille: z.string().default(''),
  photo: z.string().nullable().default(null),
  role: z.enum(['bureau', 'partenaire']),
  niveau: z.enum(['ceo', 'administrateur', 'assistant']).nullable().default(null),
  partenaire_id: z.string().nullable(),
  actif: z.boolean(),
  partenaires: z
    .object({ raison_sociale: z.string(), logo: z.string().nullable().default(null) })
    .nullable(),
});

export function versUtilisateur(ligne: z.infer<typeof ligneProfil>): Utilisateur {
  return {
    id: ligne.id,
    email: ligne.email,
    nom: ligne.nom,
    prenom: ligne.prenom,
    nomFamille: ligne.nom_famille,
    photo: adressePhotoProfil(ligne.photo),
    role: ligne.role,
    niveau: ligne.niveau,
    partenaireId: ligne.partenaire_id,
    partenaireNom: ligne.partenaires?.raison_sociale ?? null,
    partenaireLogo: adressePhotoProfil(ligne.partenaires?.logo),
    actif: ligne.actif,
  };
}

/** La copie du dernier profil lu, pour ouvrir la session sans réseau. */
const CLE_PROFIL = 'profil';

/**
 * L'état de la session, d'après la lecture du profil.
 *
 * Seule une lecture réussie qui ne trouve rien veut dire « pas de profil ».
 * Une lecture qui échoue — le plus souvent faute de réseau, au fond d'un hangar
 * ou au retour de l'appareil photo — reprend le dernier profil connu de ce
 * compte : le partenaire continue de travailler, ses gestes attendent le réseau.
 */
export function etatDepuisLecture(
  compte: { id: string; email: string },
  lecture: { data: unknown; error: unknown },
  copie: unknown
): EtatSession {
  if (!lecture.error) {
    if (lecture.data === null) return { statut: 'sans-profil', email: compte.email };
    const profil = ligneProfil.safeParse(lecture.data);
    return profil.success
      ? { statut: 'connecte', utilisateur: versUtilisateur(profil.data) }
      : { statut: 'sans-profil', email: compte.email };
  }
  const gardee = ligneProfil.safeParse(copie);
  if (gardee.success && gardee.data.id === compte.id) {
    return { statut: 'connecte', utilisateur: versUtilisateur(gardee.data) };
  }
  return { statut: 'injoignable', email: compte.email };
}

async function etatDepuisSession(session: Session | null): Promise<EtatSession> {
  if (!session) return { statut: 'anonyme' };

  let lecture: { data: unknown; error: unknown };
  try {
    lecture = await supabase()
      .from('profils')
      .select(
        'id, email, nom, prenom, nom_famille, photo, role, niveau, partenaire_id, actif, partenaires(raison_sociale, logo)'
      )
      .eq('id', session.user.id)
      .maybeSingle();
  } catch (e) {
    lecture = { data: null, error: e };
  }
  const compte = { id: session.user.id, email: session.user.email ?? '' };
  const etat = etatDepuisLecture(compte, lecture, lireCache(CLE_PROFIL)?.valeur);
  if (!lecture.error && etat.statut === 'connecte') ecrireCache(CLE_PROFIL, lecture.data);
  return etat;
}

/**
 * Après un mot de passe choisi : l'e-mail de bienvenue d'un partenaire. Le
 * serveur (functions/api/bienvenue.ts) décide — partenaire seulement, une seule
 * fois par compte. Rien n'attend la réponse, et un échec ne gêne personne.
 */
async function souhaiterBienvenue(): Promise<void> {
  try {
    const { data } = await supabase().auth.getSession();
    const jeton = data.session?.access_token;
    if (!jeton) return;
    await fetch('/api/bienvenue', {
      method: 'POST',
      headers: { Authorization: `Bearer ${jeton}` },
    });
  } catch {
    /* Sans réseau, pas de bienvenue cette fois : la prochaine fois réessaiera. */
  }
}

function resultat(erreur: Parameters<typeof messageDeRefus>[0] | null): ResultatAuth {
  return erreur ? { ok: false, message: messageDeRefus(erreur) } : { ok: true };
}

export const serviceAuthSupabase: ServiceAuth = {
  async lireSession() {
    const { data } = await supabase().auth.getSession();
    return etatDepuisSession(data.session);
  },

  surChangement(rappel) {
    const { data } = supabase().auth.onAuthStateChange((_evenement, session) => {
      /* Supabase déconseille d'appeler son propre client dans ce rappel : la
         requête attendrait le verrou que le rappel tient encore. On la
         repousse d'un tour de boucle. */
      setTimeout(() => {
        void etatDepuisSession(session).then(rappel);
      }, 0);
    });
    return () => {
      data.subscription.unsubscribe();
    };
  },

  async connexionMotDePasse(email, motDePasse) {
    const { error } = await supabase().auth.signInWithPassword({ email, password: motDePasse });
    return resultat(error);
  },

  async envoyerLienDeConnexion(email) {
    const { error } = await supabase().auth.signInWithOtp({
      email,
      /* Pas d'inscription libre : seul un compte invité par le bureau reçoit un lien. */
      options: { shouldCreateUser: false, emailRedirectTo: `${window.location.origin}/` },
    });
    return resultat(error);
  },

  async demanderReinitialisation(email) {
    const { error } = await supabase().auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/nouveau-mot-de-passe`,
    });
    return resultat(error);
  },

  async definirMotDePasse(motDePasse) {
    const { error } = await supabase().auth.updateUser({ password: motDePasse });
    if (!error) void souhaiterBienvenue();
    return resultat(error);
  },

  async deconnexion() {
    ecrireCache(CLE_PROFIL, null);
    await supabase().auth.signOut();
  },
};
